"use client";

/**
 * Saved Filters - a dropdown to save the current browse filters under a
 * name and re-apply them later, on both /listings and /map (they share
 * the same URL-param filter model). Backed by /api/saved-filters, so
 * presets persist per account across devices.
 *
 * Signed-in only (there's nowhere to store presets for an anonymous
 * visitor); renders nothing when logged out. "Apply" hands the stored
 * param set back to the parent, which writes it onto the URL.
 */
import { useEffect, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import { Bookmark, Plus, Trash2, Check } from "lucide-react";
import type { ReadonlyURLSearchParams } from "next/navigation";
import BottomSheet from "@/components/BottomSheet";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/locale-context";
import { API_BASE } from "@/lib/api";
import { useIsMobileViewport } from "@/lib/use-install-prompt";
import { useCoachMark } from "@/lib/floating-slot";
import { COUNT_BADGE, LINK, TOOLTIP } from "@/lib/ui-classes";
import {
  captureFilterParams,
  hasActiveFilters,
  filterParamsKey,
  type FilterParams,
} from "@/lib/filter-presets";

interface SavedFilter {
  id: number;
  name: string;
  params: FilterParams;
}

// One-time discoverability nudge: a user actively filtering who has never
// saved a preset probably doesn't realise they can. Pulse the bookmark +
// show a tooltip once per device, then remember the dismissal.
const NUDGE_KEY = "menzil-savedfilters-nudged";

export default function SavedFilters({
  searchParams,
  onApply,
  align = "right",
  direction = "down",
  showLabel = false,
  iconOnly = false,
}: {
  searchParams: ReadonlyURLSearchParams;
  onApply: (params: FilterParams) => void;
  // Which edge the dropdown anchors to. "right" (default) opens leftward
  // for a right-aligned actions row (/listings); "left" opens rightward so
  // it stays inside a narrow left-positioned panel (the /map filter panel)
  // instead of overflowing off-screen.
  align?: "left" | "right";
  // Vertical open direction. "down" (default) opens below the button;
  // "up" opens above it - for a sticky sheet footer where a downward
  // dropdown would be clipped off the bottom of the screen.
  direction?: "down" | "up";
  // Force the text label on (default hides it below sm).
  showLabel?: boolean;
  // Compact square icon-only button (just the bookmark + a count badge).
  iconOnly?: boolean;
}) {
  const { user } = useAuth();
  const t = useT();
  // Mobile gets a bottom sheet: the anchored dropdown's w-64 box ran
  // off the right screen edge from the toolbar position.
  const isMobile = useIsMobileViewport();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<SavedFilter[] | null>(null);
  const [limit, setLimit] = useState(5);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = captureFilterParams(searchParams);
  const currentKey = filterParamsKey(current);
  // Can save when filters are active AND not already saved verbatim.
  const alreadySaved = (items || []).some(
    (f) => filterParamsKey(f.params) === currentKey,
  );
  const count = items?.length ?? 0;
  const atLimit = count >= limit;
  const activeFilters = hasActiveFilters(searchParams);
  const canSave = activeFilters && !alreadySaved && !atLimit;
  // Eligible: signed in, presets loaded, none saved yet, actively filtering,
  // never dismissed on this device, and not in the "up" (panel footer)
  // placement, where a below-button tooltip would clip. A coach mark in
  // the interruption budget (lib/floating-slot): it comes in a session no
  // other prompt had, never on its first page, once per browser.
  const eligible =
    !!user && items !== null && count === 0 && activeFilters && !dismissed && direction === "down";
  const nudge = useCoachMark("hint-saved", eligible, "menzil.nudge.savedfilters", 16_000);
  // Pulse only while the dropdown is closed.
  const showNudge = nudge.show && !open;

  function markNudged() {
    setDismissed(true);
    nudge.done();
    try {
      localStorage.setItem(NUDGE_KEY, "1");
    } catch {
      /* storage disabled */
    }
  }

  function reload() {
    return fetch(`${API_BASE}/saved-filters`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        setItems(d.items || []);
        if (typeof d.limit === "number") setLimit(d.limit);
      })
      .catch(() => setItems([]));
  }

  useEffect(() => {
    if (user) void reload();
    else setItems(null);
  }, [user]);

  useEffect(() => {
    // Desktop dropdown only - the mobile sheet has its own backdrop
    // and closing it from this handler would fire on every sheet tap.
    if (!open || isMobile) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, isMobile]);

  // Remember a prior dismissal (per device) so the nudge stays gone. The
  // show/hide timing and the turn are the budget's (useCoachMark above).
  useEffect(() => {
    try {
      if (localStorage.getItem(NUDGE_KEY) === "1") setDismissed(true);
    } catch {
      /* ignore */
    }
  }, []);

  if (!user) return null;

  async function save() {
    const nm = name.trim();
    if (!nm) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/saved-filters`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: nm, params: current }),
      });
      setName("");
      setNaming(false);
      await reload();
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: number) {
    setBusy(true);
    try {
      await fetch(`${API_BASE}/saved-filters/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      await reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative" ref={ref}>
      {/* One-time sonar pulse drawing attention to "save this search". */}
      {showNudge && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-gray-900/40 animate-ping"
        />
      )}
      <button
        type="button"
        onClick={() => { markNudged(); setOpen((o) => !o); }}
        aria-label={iconOnly ? t("listings.savedfilters") : undefined}
        title={iconOnly ? t("listings.savedfilters") : undefined}
        className={
          iconOnly
            // 38 px square: the height of the filter pills it sits beside.
            ? "relative inline-flex h-[38px] w-[38px] items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-300 transition-colors"
            : "inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors"
        }
      >
        <Bookmark className="w-5 h-5 sm:w-4 sm:h-4" />
        {!iconOnly && (
          <span className={showLabel ? "" : "hidden sm:inline"}>{t("listings.savedfilters")}</span>
        )}
        {count > 0 &&
          (iconOnly ? (
            <span className={`absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] font-bold ${COUNT_BADGE}`}>
              {count}
            </span>
          ) : (
            <span className="text-xs text-gray-400">({count})</span>
          ))}
      </button>

      {/* One-time tooltip explaining what the bookmark does. */}
      {showNudge && (
        <div
          // Pointer sizes only: on phones the bubble over the grid read as
          // noise (owner, 2026-10-09).
          className={`pointer-events-none absolute top-full z-50 mt-2 hidden w-max max-w-[70vw] rounded-lg ${TOOLTIP} px-3 py-1.5 text-xs font-semibold leading-snug shadow-lg sm:block ${align === "left" ? "left-0" : "right-0"}`}
        >
          <span
            className={`absolute -top-1 h-2 w-2 rotate-45 bg-gray-900 ${align === "left" ? "left-4" : "right-4"}`}
          />
          {t("listings.savedfilters.nudge")}
        </div>
      )}

      {(() => {
        // Shared panel body - the desktop dropdown and the mobile
        // bottom sheet render exactly the same content.
        const body = (
          <>
            {canSave &&
              (naming ? (
                <div className="flex items-center gap-1.5 p-1.5">
                  <input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void save();
                      if (e.key === "Escape") setNaming(false);
                    }}
                    maxLength={120}
                    placeholder={t("listings.savedfilters.name")}
                    className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    disabled={busy || !name.trim()}
                    onClick={() => void save()}
                    className="rounded-md bg-indigo-600 px-2 py-1 text-xs font-semibold text-white disabled:opacity-40"
                  >
                    {t("common.save")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setNaming(true)}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                >
                  <Plus className="w-4 h-4" />
                  {t("listings.savedfilters.save")}
                </button>
              ))}

            {/* At the cap with savable filters: explain why "Save" is gone. */}
            {atLimit && hasActiveFilters(searchParams) && !alreadySaved && (
              <p className="px-2.5 py-2 text-xs text-amber-700">
                {t("listings.savedfilters.limit").replace("{n}", String(limit))}
                {limit <= 1 && (
                  <Link
                    href="/pricing"
                    className={`ml-1.5 ${LINK}`}
                  >
                    {t("listings.savedfilters.upgrade")}
                  </Link>
                )}
              </p>
            )}

            {count === 0 ? (
              <p className="px-2.5 py-2 text-xs text-gray-400">
                {t("listings.savedfilters.empty")}
              </p>
            ) : (
              <ul className="max-h-64 overflow-auto">
                {items!.map((f) => {
                  const active = filterParamsKey(f.params) === currentKey;
                  return (
                    <li key={f.id} className="group flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          onApply(f.params);
                          setOpen(false);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {active ? (
                          <Check className="w-3.5 h-3.5 shrink-0 text-emerald-600" />
                        ) : (
                          <Bookmark className="w-3.5 h-3.5 shrink-0 text-gray-300" />
                        )}
                        <span className="truncate">{f.name}</span>
                      </button>
                      {/* Always visible on touch (hover-reveal never
                          fires there); desktop keeps the quiet
                          hover-in treatment. */}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void remove(f.id)}
                        title={t("common.delete")}
                        aria-label={t("common.delete")}
                        className="rounded-lg p-1.5 text-gray-400 transition-opacity hover:text-rose-600 sm:opacity-0 sm:group-hover:opacity-100"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        );

        if (isMobile) {
          return (
            <BottomSheet
              open={open}
              onClose={() => setOpen(false)}
              title={t("listings.savedfilters")}
            >
              <div className="p-2">{body}</div>
            </BottomSheet>
          );
        }
        return open ? (
          <div
            className={`absolute ${align === "left" ? "left-0" : "right-0"} ${direction === "up" ? "bottom-full mb-1.5" : "top-full mt-1.5"} z-30 w-64 max-w-[88vw] rounded-xl border border-gray-200 bg-white p-2 shadow-lg`}
          >
            {body}
          </div>
        ) : null;
      })()}
    </div>
  );
}
